import { useMe, useLogout, useSubscription, useUsdRate } from "../hooks/useApi";
import { formatNumber, toPersianDigits } from "../utils/format";
import { PLAN_LABELS } from "../utils/plans";
import { getDisplayName } from "../utils/displayName";
import ThemeToggle from "./ThemeToggle";
import NotificationBell from "./NotificationBell";
import FixedPlanIcon from "./FixedPlanIcon";

export default function Header() {
  const { data: user } = useMe();
  const { data: usdData } = useUsdRate();
  const { data: subscription } = useSubscription();
  const logout = useLogout();

  const current = subscription?.current;
  const fixed = subscription?.current_fixed;
  const planName = current?.plan_name || "free";
  const planLabel = PLAN_LABELS[planName] || planName;
  const isFree = !current?.end_date;
  const daysLeft = current?.end_date
    ? Math.max(0, Math.ceil((new Date(current.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const fixedDaysLeft = fixed?.end_date
    ? Math.max(0, Math.ceil((new Date(fixed.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <header className="bg-surface border-b border-line px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Breadcrumb / Page title area */}
        <div className="flex items-center gap-3">
          <span className="text-h3 text-text-tertiary">
            {getDisplayName(user)}
          </span>
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-muted border border-line text-small text-text-secondary">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3h12l4 6-10 13L2 9z" />
              <path d="M11 3 8 9l4 13 4-13-3-6" />
              <path d="M2 9h20" />
            </svg>
            {planLabel}
          </span>
          {!isFree && daysLeft !== null && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-light border border-primary/20 text-small text-primary">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="hidden sm:inline">{toPersianDigits(String(daysLeft))} روز باقی</span>
              <span className="sm:hidden font-number" title={`${toPersianDigits(String(daysLeft))} روز باقی`}>
                {toPersianDigits(String(daysLeft))}
              </span>
            </span>
          )}
          {fixed && (
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-small"
              title={toPersianDigits(fixed.plan_name)}
              style={{ background: "rgb(var(--chart-blue) / 0.12)", color: "rgb(var(--chart-blue))", border: "1px solid rgb(var(--chart-blue) / 0.35)" }}
            >
              <FixedPlanIcon size={14} />
              <span className="font-semibold">ثابت</span>
              <span className="hidden md:inline">{toPersianDigits(fixed.plan_name)}</span>
              {fixedDaysLeft !== null && (
                <span className="hidden md:inline text-text-secondary">
                  · {toPersianDigits(String(fixedDaysLeft))} روز
                </span>
              )}
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <NotificationBell />

          {/* Theme toggle */}
          <ThemeToggle />

          {/* USD Rate pill — replaces wallet balance in header (desktop) */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-muted border border-line text-small text-text-secondary"
            title={usdData ? `${Number(usdData.usd_rate_irr).toLocaleString("fa-IR")} ریال / ${Number(usdData.usd_rate_toman).toLocaleString("fa-IR")} تومان` : "نرخ دلار"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
            <span className="hidden lg:inline text-text-tertiary">نرخ دلار</span>
            <span className="font-number">
              {usdData ? formatNumber(usdData.usd_rate_toman) : "—"}
            </span>
            <span className="text-text-tertiary">تومان</span>
          </div>

          {/* Logout */}
          <button
            onClick={() => logout.mutate()}
            className="flex items-center gap-1.5 text-small text-text-tertiary hover:text-status-error-text transition-colors"
            title="خروج"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            خروج
          </button>
        </div>
      </div>
    </header>
  );
}
